import { useMapAddressContext } from '@delmonicos/shared-design-system-react/src/components/location/interactive-map/map-address.context';
import { AddressParts } from '@delmonicos/shared-design-system-react/src/components/location/interactive-map/use-map-with-address';
import { useDebounce } from '@delmonicos/shared-sdk/libs/core/src/hooks';
import { useCallback, useEffect } from 'react';
import { FieldPathByValue, FieldValues, useFormContext } from 'react-hook-form';

import countries from '@app/core/countries/countries';
import {
  LocationMapAddressFields,
  LocationMapCoordinates,
  addressFieldsEqual,
  coordinatesEqual,
} from '@app/feature/location/location-map-sync.util';
import { EchoGuard, useEchoGuard } from '@app/feature/location/use-echo-guard';

// All six fields are string-valued on every location form (TextInput-backed,
// coordinates included — they're kept as form strings, not numbers, same as
// address/city/etc), though update forms make them optional (Partial<...>)
// where create forms don't — hence `string | undefined` rather than plain
// `string`. FieldPathByValue still pins the *shape* down per-form, so a form
// with a differently-typed field at one of these paths fails to compile
// instead of silently reading/writing the wrong thing.
export interface LocationMapFieldPaths<TFieldValues extends FieldValues> {
  address: FieldPathByValue<TFieldValues, string | undefined>;
  city: FieldPathByValue<TFieldValues, string | undefined>;
  postalCode: FieldPathByValue<TFieldValues, string | undefined>;
  country: FieldPathByValue<TFieldValues, string | undefined>;
  latitude: FieldPathByValue<TFieldValues, string | undefined>;
  longitude: FieldPathByValue<TFieldValues, string | undefined>;
}

// Widens a field path from the narrow FieldPathByValue union to plain
// `string` — a safe upcast (a string-literal union is always assignable to
// `string`) that keeps setValue/trigger's own generic inference from trying
// (and failing) to destructure an abstract TFieldValues-derived literal type
// through their PathValue machinery. Pure/stable so it can live outside any
// hook without upsetting exhaustive-deps.
const asPath = <TFieldValues extends FieldValues>(
  path: FieldPathByValue<TFieldValues, string | undefined>,
): string => path;

export interface UseLocationMapSyncResult {
  addressGuard: EchoGuard<LocationMapAddressFields>;
  coordinatesGuard: EchoGuard<LocationMapCoordinates>;
  // Map → form. Wire to the GeoMap's onAddressChange.
  handleAddressChange: (addr: Partial<AddressParts>) => void;
}

/**
 * Map → form half of the sync, and the guards shared with the form → map
 * half (LocationMapFormSync below). See use-echo-guard.ts for why this is
 * echo-guard-based rather than a "suppress next" flag.
 *
 * Internally reads the form context untyped: react-hook-form's Path/PathValue
 * conditional types can't resolve through this extra layer of user generics
 * (TFieldValues is still abstract inside this hook's body). The
 * LocationMapFieldPaths<TFieldValues> constraint on the `fieldPaths` argument
 * is what actually keeps call sites honest (typo'd or wrong-typed paths fail
 * to compile at CREATE_LOCATION_MAP_FIELD_PATHS/UPDATE_LOCATION_MAP_FIELD_PATHS);
 * fighting react-hook-form's generics for the reads/writes inside here too
 * wouldn't add any real safety on top of that.
 */
export const useLocationMapSync = <TFieldValues extends FieldValues>(
  fieldPaths: LocationMapFieldPaths<TFieldValues>,
): UseLocationMapSyncResult => {
  const { getValues, setValue, trigger } = useFormContext();

  // Track what's known to be in sync between the form and the map for each
  // field group. See use-echo-guard.ts — this is what lets both directions
  // of the sync (form → map and map → form) tell a stale/duplicate echo
  // apart from real new information, without a "suppress the next one" flag
  // that can get stuck if the geocode it's waiting on never calls back.
  const addressGuard = useEchoGuard<LocationMapAddressFields>(
    {
      address: getValues(fieldPaths.address) ?? '',
      city: getValues(fieldPaths.city) ?? '',
      postalCode: getValues(fieldPaths.postalCode) ?? '',
      country: getValues(fieldPaths.country) ?? '',
    },
    addressFieldsEqual,
  );
  const coordinatesGuard = useEchoGuard<LocationMapCoordinates>(
    {
      latitude: Number(getValues(fieldPaths.latitude) ?? 0),
      longitude: Number(getValues(fieldPaths.longitude) ?? 0),
    },
    coordinatesEqual,
  );

  const handleAddressChange = useCallback(
    (addr: Partial<AddressParts>) => {
      const nextAddress: LocationMapAddressFields = {
        address: addr.address?.trim() ?? '',
        city: addr.city?.trim() ?? '',
        postalCode: addr.postalCode?.trim() ?? '',
        country: addr.country
          ? (countries.alpha2ToAlpha3(addr.country) ?? '')
          : '',
      };

      // Only accept the map's address echo if the field group isn't already
      // ahead of it — i.e. the user hasn't got a fresher, not-yet-pushed
      // edit sitting there. If it is, let that edit finish its own
      // round-trip instead of clobbering it with older map data.
      const currentAddress: LocationMapAddressFields = {
        address: getValues(fieldPaths.address) ?? '',
        city: getValues(fieldPaths.city) ?? '',
        postalCode: getValues(fieldPaths.postalCode) ?? '',
        country: getValues(fieldPaths.country) ?? '',
      };

      if (addressGuard.isSynced(currentAddress)) {
        addressGuard.recordSynced(nextAddress);
        setValue(asPath(fieldPaths.address), nextAddress.address, {
          shouldDirty: true,
        });
        setValue(asPath(fieldPaths.city), nextAddress.city, {
          shouldDirty: true,
        });
        setValue(asPath(fieldPaths.postalCode), nextAddress.postalCode, {
          shouldDirty: true,
        });
        setValue(asPath(fieldPaths.country), nextAddress.country, {
          shouldDirty: true,
        });
      }

      if (
        addr.coordinates &&
        Number.isFinite(addr.coordinates.latitude) &&
        Number.isFinite(addr.coordinates.longitude)
      ) {
        const nextCoordinates: LocationMapCoordinates = {
          latitude: addr.coordinates.latitude,
          longitude: addr.coordinates.longitude,
        };
        const liveLatitude = Number(getValues(fieldPaths.latitude));
        const liveLongitude = Number(getValues(fieldPaths.longitude));
        // Blank/untouched fields (e.g. a brand new location, nothing typed
        // yet) parse to NaN, which never equals anything — there's no
        // pending edit to protect there, so always accept the map's value.
        // Otherwise only accept it if the field isn't already ahead of it.
        const hasLiveCoordinates =
          Number.isFinite(liveLatitude) && Number.isFinite(liveLongitude);

        if (
          !hasLiveCoordinates ||
          coordinatesGuard.isSynced({
            latitude: liveLatitude,
            longitude: liveLongitude,
          })
        ) {
          coordinatesGuard.recordSynced(nextCoordinates);
          setValue(
            asPath(fieldPaths.latitude),
            nextCoordinates.latitude.toFixed(6),
            { shouldDirty: true },
          );
          setValue(
            asPath(fieldPaths.longitude),
            nextCoordinates.longitude.toFixed(6),
            { shouldDirty: true },
          );
        }
      }

      trigger([
        asPath(fieldPaths.address),
        asPath(fieldPaths.city),
        asPath(fieldPaths.postalCode),
        asPath(fieldPaths.country),
        asPath(fieldPaths.latitude),
        asPath(fieldPaths.longitude),
      ]);
    },
    [setValue, getValues, trigger, addressGuard, coordinatesGuard, fieldPaths],
  );

  return { addressGuard, coordinatesGuard, handleAddressChange };
};

export interface LocationMapFormSyncProps<TFieldValues extends FieldValues> {
  fieldPaths: LocationMapFieldPaths<TFieldValues>;
  addressGuard: EchoGuard<LocationMapAddressFields>;
  coordinatesGuard: EchoGuard<LocationMapCoordinates>;
}

/**
 * Form → map half of the sync (debounced so the pin/address input doesn't
 * jump around mid-keystroke). Must be rendered as a child of
 * <GeoMap interactive> — i.e. inside MapAddressProvider — since it reads
 * useMapAddressContext(). Pair with useLocationMapSync above, passing along
 * the same fieldPaths/addressGuard/coordinatesGuard.
 */
export const LocationMapFormSync = <TFieldValues extends FieldValues>({
  fieldPaths,
  addressGuard,
  coordinatesGuard,
}: LocationMapFormSyncProps<TFieldValues>): null => {
  const { updateAddress, handleCoordsChange } = useMapAddressContext();
  // See useLocationMapSync's doc comment — untyped form context on purpose.
  const { watch } = useFormContext();

  const addressValue = watch(fieldPaths.address);
  const cityValue = watch(fieldPaths.city);
  const postalCodeValue = watch(fieldPaths.postalCode);
  const countryValue = watch(fieldPaths.country);
  const debouncedAddress = useDebounce(addressValue, 600);
  const debouncedCity = useDebounce(cityValue, 600);
  const debouncedPostalCode = useDebounce(postalCodeValue, 600);
  const debouncedCountry = useDebounce(countryValue, 600);

  useEffect(() => {
    // Tracked in the form's own representation (country as alpha-3) — the
    // guard must compare like-for-like with handleAddressChange, which reads
    // straight off the form.
    const next: LocationMapAddressFields = {
      address: debouncedAddress ?? '',
      city: debouncedCity ?? '',
      postalCode: debouncedPostalCode ?? '',
      country: debouncedCountry ?? '',
    };

    // Already what the map last told us (or already pushed) — pushing again
    // would just re-trigger a forward-geocode that could snap the pin to a
    // different point than the one that produced this address text.
    if (addressGuard.isSynced(next)) return;

    addressGuard.recordSynced(next);
    updateAddress({
      ...next,
      country: debouncedCountry
        ? (countries.alpha3ToAlpha2(debouncedCountry) ?? '')
        : '',
    });
  }, [
    updateAddress,
    debouncedAddress,
    debouncedCity,
    debouncedPostalCode,
    debouncedCountry,
    addressGuard,
  ]);

  // Syncs manually typed coordinates → map marker (debounced so the marker
  // doesn't jump around while the user is mid-keystroke)
  const latitudeValue = watch(fieldPaths.latitude);
  const longitudeValue = watch(fieldPaths.longitude);
  const debouncedLatitude = useDebounce(latitudeValue, 600);
  const debouncedLongitude = useDebounce(longitudeValue, 600);

  useEffect(() => {
    const lat = Number(debouncedLatitude);
    const lng = Number(debouncedLongitude);

    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      return;
    }

    const next: LocationMapCoordinates = { latitude: lat, longitude: lng };

    if (coordinatesGuard.isSynced(next)) return;

    coordinatesGuard.recordSynced(next);
    handleCoordsChange({ lat, lng });
  }, [
    debouncedLatitude,
    debouncedLongitude,
    handleCoordsChange,
    coordinatesGuard,
  ]);

  return null;
};
